import { useState } from 'react';
import { useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ShieldOff, Trash2 } from 'lucide-react';
import { api } from '@/lib/api';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { UserMention } from '@/components/UserMention';
import { UserPicker } from '@/components/UserPicker';
import { useGuildRights } from '@/lib/access';

/**
 * Permaban list — persistent bans that re-apply if the user rejoins.
 * The write endpoints go through /api/actions/permaban and /permaunban so
 * the bot can perform the actual Discord ban/unban at the same time.
 */

interface PermaBan {
  guildId: string;
  userId: string;
  reason: string;
  addedBy: string | null;
  createdAt: string;
}

export function Permabans() {
  const { guildId } = useParams<{ guildId: string }>();
  const qc = useQueryClient();
  const canEdit = useGuildRights(guildId).can('permabans', 'edit');

  const listQuery = useQuery({
    queryKey: ['permabans', guildId],
    queryFn: () => api<{ permabans: PermaBan[] }>(`/api/guilds/${guildId}/permabans`),
    enabled: !!guildId,
  });

  const [userId, setUserId] = useState<string | null>(null);
  const [reason, setReason] = useState('');
  const [error, setError] = useState<string | null>(null);

  const addMut = useMutation({
    mutationFn: (body: { userId: string; reason?: string }) =>
      api('/api/actions/permaban', {
        method: 'POST',
        body: JSON.stringify({ guildId, ...body }),
      }),
    onSuccess: () => {
      setUserId(null);
      setReason('');
      setError(null);
      // Bot fires an event on the LiveStream once done, but for immediate
      // feedback we refresh the list a moment later.
      setTimeout(() => qc.invalidateQueries({ queryKey: ['permabans', guildId] }), 800);
    },
    onError: (err: Error) => setError(err.message),
  });

  const removeMut = useMutation({
    mutationFn: (targetUserId: string) =>
      api('/api/actions/permaunban', {
        method: 'POST',
        body: JSON.stringify({ guildId, userId: targetUserId }),
      }),
    onSuccess: () => {
      setTimeout(() => qc.invalidateQueries({ queryKey: ['permabans', guildId] }), 800);
    },
  });

  const items = listQuery.data?.permabans ?? [];

  return (
    <div className="mx-auto max-w-3xl">
      <div className="mb-4 flex items-baseline justify-between">
        <h1 className="flex items-center gap-2 text-2xl font-semibold">
          <ShieldOff className="h-5 w-5" /> Permabans
        </h1>
        <span className="text-xs text-muted-foreground">
          {items.length} aktiv
        </span>
      </div>

      {canEdit ? (
        <Card className="mb-4">
          <CardHeader>
            <CardTitle className="text-lg">Neuen Permaban ausstellen</CardTitle>
            <CardDescription>
              Der User wird sofort gebannt (falls Mitglied) und bei jedem
              Wiederbeitritt automatisch erneut gebannt. Nur Admins.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            <div>
              <Label>User</Label>
              <UserPicker
                guildId={guildId!}
                value={userId}
                onChange={setUserId}
                placeholder="Suche User oder Discord-ID (auch nicht-Mitglieder)"
              />
            </div>
            <div>
              <Label>Grund</Label>
              <Textarea
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder="Warum permanent gebannt?"
                className="min-h-[60px]"
                maxLength={500}
              />
            </div>
            {error && <p className="text-sm text-destructive">{error}</p>}
            <div className="flex justify-end">
              <Button
                variant="destructive"
                disabled={!userId || addMut.isPending}
                onClick={() => {
                  if (confirm(`User ${userId} wirklich permanent bannen?`)) {
                    addMut.mutate({ userId: userId!, reason: reason || undefined });
                  }
                }}
              >
                {addMut.isPending ? 'Banne…' : 'Permaban ausstellen'}
              </Button>
            </div>
          </CardContent>
        </Card>
      ) : (
        <Card className="mb-4">
          <CardContent className="pt-6 text-sm text-muted-foreground">
            Neue Permabans können nur von Admins ausgestellt werden.
          </CardContent>
        </Card>
      )}

      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Liste</CardTitle>
        </CardHeader>
        <CardContent>
          {listQuery.isLoading && <p className="text-muted-foreground">Lade…</p>}
          {items.length === 0 && !listQuery.isLoading && (
            <p className="text-sm text-muted-foreground">Keine Permabans.</p>
          )}
          <ul className="space-y-2">
            {items.map((p) => (
              <li
                key={p.userId}
                className="flex items-start justify-between gap-2 rounded-md border p-3 text-sm"
              >
                <div className="min-w-0 flex-1 space-y-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <UserMention id={p.userId} />
                    {p.addedBy && (
                      <span className="text-xs text-muted-foreground">
                        von <UserMention id={p.addedBy} />
                      </span>
                    )}
                    <span className="text-xs text-muted-foreground">
                      · {new Date(p.createdAt).toLocaleString('de-DE')}
                    </span>
                  </div>
                  <div className="whitespace-pre-wrap">{p.reason}</div>
                </div>
                {canEdit && (
                  <Button
                    variant="ghost"
                    size="icon"
                    disabled={removeMut.isPending}
                    onClick={() => {
                      if (confirm(`Permaban für ${p.userId} aufheben?`)) {
                        removeMut.mutate(p.userId);
                      }
                    }}
                    title="Permaban aufheben"
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                )}
              </li>
            ))}
          </ul>
        </CardContent>
      </Card>
    </div>
  );
}
